/**
 * Session state for the app.
 *
 * The session lives in an httpOnly cookie the JavaScript cannot read, so
 * "am I signed in?" is answerable only by asking the server. GET /auth/me on
 * mount is that question.
 *
 * `status` is deliberately three-valued. Collapsing 'loading' into
 * 'signed_out' would flash the login screen on every page load before the
 * answer came back, and would do it hardest on a slow connection.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api, type Capability, type Session, type BusinessType } from '../api.js';

type AuthStatus = 'loading' | 'authenticated' | 'signed_out';

interface AuthValue {
  status: AuthStatus;
  session: Session | null;
  /** Called after the second factor succeeds, to pick up the real session. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Whether the session may do this (0069). The API refuses what it may not
   * either way; this only decides what a screen offers.
   */
  can: (capability: Capability) => boolean;
}

/** A session's capability; one without the field is a test's hand-made admin. */
export function sessionCan(session: Session | null, capability: Capability): boolean {
  if (!session) return false;
  return session.capabilities ? session.capabilities.includes(capability) : true;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<Session | null>(null);

  const refresh = useCallback(async () => {
    try {
      setSession(await api.me());
      setStatus('authenticated');
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        setSession(null);
        setStatus('signed_out');
        return;
      }
      // A network failure is not a sign-out. Treating it as one would drop
      // the operator onto the login screen every time the API restarts, and
      // they would sign in again to fix a problem that was never theirs.
      setStatus((current) => (current === 'loading' ? 'signed_out' : current));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Local state clears either way. If the call failed the cookie may
      // survive, but leaving the UI signed in after the operator asked to
      // leave is the worse of the two failures.
      setSession(null);
      setStatus('signed_out');
    }
  }, []);

  const value = useMemo(
    () => ({ status, session, refresh, signOut, can: (capability: Capability) => sessionCan(session, capability) }),
    [status, session, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}

/**
 * What kind of business the session is in (0078), for a screen deciding what
 * to offer: the shareholder report and Form 940 are not offered to a
 * non-profit. Undefined without a provider or on a hand-made session, which a
 * screen treats as an S corporation, as `useCan` treats it as an admin; null
 * when the type was never recorded, where the API's refusal says so.
 */
export function useBusiness(): BusinessType | null | undefined {
  return useContext(AuthContext)?.session?.business;
}

/** Whether every line names a fund (0079). False without a provider, as a business that keeps none. */
export function useUsesFunds(): boolean {
  return useContext(AuthContext)?.session?.usesFunds ?? false;
}

/** Whether the business keeps goods (0086). False without a provider, as a business that keeps none. */
export function useKeepsGoods(): boolean {
  return useContext(AuthContext)?.session?.keepsGoods ?? false;
}

/** What a business calls its projects on screen (0081): Program for a non-profit, Project for any other, until the naming setting is built (§B.15.7). */
export function projectWord(business: BusinessType | null | undefined): 'Program' | 'Project' {
  return business?.taxStatus === 'tax_exempt' ? 'Program' : 'Project';
}

/** Whether the books are a non-profit's: net assets by class, and its own statements (N3). */
export function keepsNetAssets(business: BusinessType | null | undefined): boolean {
  return business?.taxStatus === 'tax_exempt';
}

/** Whether the business files Form 990-PF and keeps its markings and disqualified persons (0085). */
export function filesForm990Pf(business: BusinessType | null | undefined): boolean {
  return business?.foundationStatus === 'private_nonoperating_foundation' || business?.foundationStatus === 'private_operating_foundation';
}

/**
 * Whether the shareholder report applies: it is an S corporation's (Form
 * 1120-S, K-1, 7203), and the API answers 404 for any other type (0094). An
 * unrecorded type is offered it, and the API's refusal says why.
 */
export function offersShareholderReport(business: BusinessType | null | undefined): boolean {
  return business == null || business.taxStatus === 's_corporation';
}

/** Whether the partners' capital statement and Settings' Partners apply: a partnership's (0096). */
export function offersPartnersCapital(business: BusinessType | null | undefined): boolean {
  return business?.taxStatus === 'partnership';
}

/** Whether the owner's equity statement applies: a business taxed with its owner's (0094). */
export function offersOwnersEquity(business: BusinessType | null | undefined): boolean {
  return business?.taxStatus === 'disregarded_entity';
}

/** Whether Form 940 applies: a 501(c)(3) owes no FUTA. */
export function offersForm940(business: BusinessType | null | undefined): boolean {
  return business?.exemptSection !== '501c3';
}

/**
 * What the session may do, for a screen deciding what to offer (0069).
 *
 * Without a provider -- a component test that renders one screen alone --
 * everything is offered, as to an admin: the app always renders inside the
 * provider, and the API refuses what a session may not do either way, so this
 * only ever decides what a screen shows. Tests of what a role is NOT offered
 * mock `useAuth` and render inside it.
 */
export function useCan(): (capability: Capability) => boolean {
  const value = useContext(AuthContext);
  return value ? value.can : () => true;
}
